import React, { useState, useEffect } from 'react';
import styles from './DashboardGraphs.module.css';

// API 데이터 타입 정의
interface ChartDataPoint {
  x: number;
  y: number;
  date: string;
}

// 강의별 평균 시청 시간 API 타입
interface CourseWatchTimeData {
  lecture_id: number;
  lecture_title: string;
  avg_watch_time: number; // 분 단위
}

interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 시간 포맷팅 함수 (시간, 분, 초 단위)
const formatWatchTime = (minutes: number): string => {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)}초`;
  }
  
  const totalMinutes = Math.floor(minutes);
  const seconds = Math.round((minutes - totalMinutes) * 60);
  
  // 시간 단위 계산
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  
  if (hours > 0) {
    // 1시간 이상인 경우 - 초 단위 제거
    if (remainingMinutes > 0) {
      return `${hours}시간 ${remainingMinutes}분`;
    } else {
      return `${hours}시간`;
    }
  } else {
    // 1시간 미만인 경우 - 초 단위 포함
    if (seconds > 0) {
      return `${remainingMinutes}분 ${seconds}초`;
    } else {
      return `${remainingMinutes}분`;
    }
  }
};

// 임시 API 데이터 (나중에 실제 API로 교체) - 30일 데이터
const mockApiData: ChartDataPoint[] = [
  { x: 0, y: 320, date: '9월 1일' },
  { x: 1, y: 310, date: '9월 2일' },
  { x: 2, y: 330, date: '9월 3일' },
  { x: 3, y: 340, date: '9월 4일' },
  { x: 4, y: 350, date: '9월 5일' },
  { x: 5, y: 290, date: '9월 6일' },
  { x: 6, y: 270, date: '9월 7일' },
  { x: 7, y: 260, date: '9월 8일' },
  { x: 8, y: 290, date: '9월 9일' },
  { x: 9, y: 310, date: '9월 10일' },
  { x: 10, y: 320, date: '9월 11일' },
  { x: 11, y: 250, date: '9월 12일' },
  { x: 12, y: 220, date: '9월 13일' },
  { x: 13, y: 200, date: '9월 14일' },
  { x: 14, y: 210, date: '9월 15일' },
  { x: 15, y: 230, date: '9월 16일' },
  { x: 16, y: 260, date: '9월 17일' },
  { x: 17, y: 180, date: '9월 18일' },
  { x: 18, y: 160, date: '9월 19일' },
  { x: 19, y: 150, date: '9월 20일' },
  { x: 20, y: 170, date: '9월 21일' },
  { x: 21, y: 190, date: '9월 22일' },
  { x: 22, y: 220, date: '9월 23일' },
  { x: 23, y: 240, date: '9월 24일' },
  { x: 24, y: 280, date: '9월 25일' },
  { x: 25, y: 300, date: '9월 26일' },
  { x: 26, y: 320, date: '9월 27일' },
  { x: 27, y: 360, date: '9월 28일' },
  { x: 28, y: 380, date: '9월 29일' },
  { x: 29, y: 390, date: '9월 30일' },
];

// Y값을 SVG 좌표로 변환 (400~0 범위를 16~160 SVG 좌표로)
const scaleY = (value: number, maxValue: number = 400, minValue: number = 0): number => {
  const svgHeight = 160 - 16; // 실제 그래프 영역 높이
  const normalizedValue = (value - minValue) / (maxValue - minValue);
  return 160 - (normalizedValue * svgHeight); // Y축 뒤집기
};

// X값을 SVG 좌표로 변환 (0~29 범위를 0~609 SVG 좌표로)
const scaleX = (index: number, totalPoints: number, maxWidth: number = 609): number => {
  return (index / (totalPoints - 1)) * maxWidth;
};

// SVG Path 문자열 생성
const generatePath = (data: ChartDataPoint[]): string => {
  const scaledPoints = data.map((point, index) => ({
    x: scaleX(index, data.length),
    y: scaleY(point.y)
  }));

  let path = `M ${scaledPoints[0].x} ${scaledPoints[0].y}`;
  
  // 베지어 곡선으로 부드러운 라인 생성
  for (let i = 1; i < scaledPoints.length; i++) {
    const prevPoint = scaledPoints[i - 1];
    const currentPoint = scaledPoints[i];
    const controlPointX = (prevPoint.x + currentPoint.x) / 2;
    
    path += ` C ${controlPointX} ${prevPoint.y}, ${controlPointX} ${currentPoint.y}, ${currentPoint.x} ${currentPoint.y}`;
  }
  
  return path;
};

// 영역 채우기용 Path 생성
const generateAreaPath = (data: ChartDataPoint[]): string => {
  const linePath = generatePath(data);
  const lastX = scaleX(data.length - 1, data.length);
  return `${linePath} L ${lastX} 160 L 0 160 Z`;
};

const DashboardGraphs: React.FC = () => {
  // 기존 차트 데이터 (일일 활성 사용자)
  const chartData = mockApiData;
  
  // 강의별 평균 시청 시간 상태
  const [courseData, setCourseData] = useState<CourseWatchTimeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 강의별 평균 시청 시간 API 호출
  const fetchCourseWatchTime = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 강의별 평균 시청 시간 API 호출 시작...');
      
      const response = await fetch('https://campus-dev.duckdns.org/api/admin/average-watch-time/course', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API 요청 실패: ${response.status} ${response.statusText}`);
      }

      const result: ApiResponse<CourseWatchTimeData[]> = await response.json();
      console.log('✅ 강의별 평균 시청 시간 API 응답:', result);
      
      setCourseData(result.data);
      console.log('📊 강의 데이터 설정 완료:', result.data);
      
    } catch (err) {
      console.error('❌ 강의별 평균 시청 시간 API 실패:', err);
      setError('강의별 평균 시청 시간 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 API 데이터 로딩
  useEffect(() => {
    console.log('🎬 DashboardGraphs useEffect 실행');
    fetchCourseWatchTime();
  }, []);
  
  // 바 너비 계산 함수 (실제 데이터 기반)
  const getBarWidth = (minutes: number): string => {
    if (courseData.length === 0) return '0%';
    
    const maxMinutes = Math.max(...courseData.map(course => course.avg_watch_time));
    
    if (maxMinutes === 0) return '10%'; // 모든 데이터가 0인 경우 최소 너비
    
    const percentage = (minutes / maxMinutes) * 100;
    return `${Math.max(5, percentage)}%`; // 최소 5% 너비 보장
  };
  
  return (
    <div className={styles.statgraph}>
      <div className={styles.sectionHeadersectionHeader} />
      
      {/* 일일 활성 사용자 수 그래프 */}
      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>일일 활성 사용자 수</div>
          <div className={styles.subtitle}>9월 1일 ~ 9월 30일</div>
        </div>
        <div className={styles.graph}>
          {/* Y축 라벨들 */}
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>400</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>300</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>200</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>100</div>
            <div className={styles.dividerdivider} />
          </div>
          <div className={styles.yAxisItem}>
            <div className={styles.yAxisLabel}>0</div>
            <div className={styles.dividerdivider} />
          </div>
          
          {/* 그래프 영역 */}
          <div className={styles.graphPlaceholder}>
            <svg className={styles.chartSvg} viewBox="0 0 609 184">
              {/* 그라데이션 정의 */}
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C5E0E0" stopOpacity="0.8" />
                  <stop offset="50%" stopColor="#C5E0E0" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#C5E0E0" stopOpacity="0" />
                </linearGradient>
              </defs>
              
              {/* 영역 채우기 */}
              <path
                d={generateAreaPath(chartData)}
                fill="url(#areaGradient)"
                className={styles.chartArea}
              />
              
              {/* 라인 */}
              <path
                d={generatePath(chartData)}
                fill="none"
                stroke="#448181"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.chartLine}
              />
              
              {/* 데이터 포인트들 */}
              {chartData.map((point, index) => (
                <circle
                  key={index}
                  cx={scaleX(index, chartData.length)}
                  cy={scaleY(point.y)}
                  r="2.5"
                  fill="#448181"
                  className={styles.dataPoint}
                  style={{ animationDelay: `${2.8 + index * 0.02}s` }}
                />
              ))}
            </svg>
          </div>
        </div>
      </div>
      
      {/* 강의별 평균 시청 시간 바 차트 - 실제 API 데이터 */}
      <div className={styles.card1}>
        <div className={styles.heading1}>
          <div className={styles.title}>강의별 평균 시청 시간</div>
          <div className={styles.subtitle}>상위 5개 강의</div>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7d7d7d' }}>
            강의 데이터 로딩 중...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ff4444' }}>
            <div>❌ {error}</div>
            <button 
              onClick={fetchCourseWatchTime}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#448181',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 다시 시도
            </button>
          </div>
        ) : (
          <div className={styles.graph1}>
            {courseData.map((course, index) => (
              <div key={course.lecture_id} className={styles.barItem}>
                <div className={styles.barLabel}>{course.lecture_title}</div>
                <div className={styles.barWrapper}>
                  <div 
                    style={{
                      width: getBarWidth(course.avg_watch_time),
                      backgroundColor: '#5C9A9A',
                      height: '12px',
                      borderRadius: '6px',
                      transformOrigin: 'right',
                      animation: `barGrowth 1.2s ease-out ${index * 0.15}s both`,
                      position: 'relative',
                      flex: 'none'
                    }}
                  />
                </div>
                <div className={styles.barValue}>{formatWatchTime(course.avg_watch_time)}</div>
              </div>
            ))}
            
            {/* 5개 미만일 경우 빈 항목으로 채우기 */}
            {Array.from({ length: Math.max(0, 5 - courseData.length) }).map((_, index) => (
              <div key={`empty-${index}`} className={styles.barItem}>
                <div className={styles.barLabel}>-</div>
                <div className={styles.barWrapper}>
                  <div 
                    style={{
                      width: '5%',
                      backgroundColor: '#E5E5E5',
                      height: '12px',
                      borderRadius: '6px',
                      opacity: 0.3,
                      position: 'relative',
                      flex: 'none'
                    }}
                  />
                </div>
                <div className={styles.barValue}>0분</div>
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.trailing}>
          <div className={styles.change}>+10%</div>
          <div className={styles.period}>전일 대비</div>
        </div>
      </div>
    </div>
  );
};

export default DashboardGraphs;